import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { HostInfo } from '@hqo/hqo-miniapp-client-sdk';
import { captureMessage } from '@sentry/react';
import { useMiniappSdk, useMiniappSdkEventHandler } from 'hooks/use-miniapp-sdk.hook';
import { HOST_PLATFORMS, HostPlatform } from 'shared/consts';
import { HostInfoContext } from './host-info.context';

/**
 * Asks the host what it is, once, and exposes the answer.
 *
 * Until now the app had no way to tell a native shell from a narrow browser window, so it chose its chrome
 * by viewport width and ended up drawing an in-app back button underneath the native one. This is the
 * signal that distinguishes them.
 */
export const HostInfoProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [hostPlatform, setHostPlatform] = useState<HostPlatform>();
  const [hostInfo, setHostInfo] = useState<HostInfo>();
  const [isHostInfoSettled, setIsHostInfoSettled] = useState(false);
  const client = useMiniappSdk();

  useEffect(() => {
    if (client && !hostPlatform) {
      client.host.getInfo();
    } else if (!client) {
      // No client at all — outside a host, or before ui-metadata supplies the app uuid. Settle so nothing
      // waits forever on an answer that is never coming.
      setIsHostInfoSettled(true);
    }
  }, [client, hostPlatform]);

  useMiniappSdkEventHandler(
    'getHostInfoResponse',
    (data: unknown) => {
      setIsHostInfoSettled(true);

      // The payload should be an object, but a double-encoded one can arrive as a non-empty string, and
      // reaching into a string with `in` throws. Narrow to an object before touching it.
      if (data && typeof data === 'object') {
        // The hosts do not answer in the same shape, and neither the SDK's types nor the fleet's readers
        // describe both. `tenant-web`, which runs inside the native shell, reads `hostInfo.host.os_name`
        // and its `HostInfo` type carries NO `platform` at all — so on native the answer is nested and
        // identifies itself by OS. `platform` comes from the web and tenant-portal hosts instead, and
        // every miniapp reading it off the top level simply gets undefined on a device.
        const payload = data as {
          platform?: HostPlatform;
          host?: { platform?: HostPlatform; os_name?: string };
        };
        const host = payload.host ?? payload;
        // An OS means a native shell, which is the one case `platform` never covers.
        const reportedPlatform = payload.host?.platform ?? payload.platform;
        const platform = reportedPlatform ?? (payload.host?.os_name ? HOST_PLATFORMS.MOBILE : undefined);

        if (platform) {
          setHostPlatform(platform);
        } else {
          // Nothing in the payload identifies the host. Report its keys rather than let the app quietly
          // default to desktop forever with no way to find out why.
          captureMessage('getHostInfoResponse: host not identifiable from payload', {
            level: 'warning',
            extra: { keys: Object.keys(payload), hostKeys: payload.host ? Object.keys(payload.host) : null },
          });
        }

        setHostInfo(host as HostInfo);
      } else if (data != null) {
        captureMessage('getHostInfoResponse: unexpected non-object payload', {
          level: 'warning',
          extra: { payloadType: typeof data },
        });
      }
    },
    [setHostPlatform, setHostInfo, setIsHostInfoSettled],
  );

  const value = useMemo(
    () => ({ hostPlatform, hostInfo, isHostInfoSettled }),
    [hostPlatform, hostInfo, isHostInfoSettled],
  );

  return <HostInfoContext.Provider value={value}>{children}</HostInfoContext.Provider>;
};
